/**
 * service-orders-store — hooks React Query pra OS digital (R5).
 *
 * Hooks:
 * - useServiceOrders(filters) — lista
 * - useServiceOrder(id) — single
 * - useServiceOrderActions() — create/changeStatus/submitSignature/external links
 * - useOsExternalLinks(osId) — links públicos (admin)
 */

import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from './api';

// ─── Tipos ─────────────────────────────────────────────────────────────

export type OsStatus = 'aberta' | 'atribuida' | 'em_campo' | 'concluida' | 'cancelada';
export type OsPrioridade = 'baixa' | 'normal' | 'alta' | 'urgente';

export interface ServiceOrder {
  id: string;
  /**
   * FEAT-V2-034 / BUG-V2-045: número sequencial humano (ex: 1000) preenchido
   * pelo backend. NULL pra OSs antigas pré-migration 050.
   */
  numeroHumano?: number | null;
  /**
   * FEAT-V2-034 / BUG-V2-045: label pronto pra exibir (ex: "#1000").
   * Pré-resolvido pelo backend (shape()) pra UI. Fallback `id` UUID quando
   * `numero_humano` IS NULL. UI **DEVE** preferir `humanLabel ?? id` em
   * qualquer render textual.
   */
  humanLabel?: string;
  workflowId: string;
  clienteId: string;
  tecnicoId: string | null;
  criadoPorId: string | null;
  tipo: string;
  titulo: string;
  descricao: string | null;
  endereco: string;
  latitude: number | null;
  longitude: number | null;
  prioridade: OsPrioridade;
  agendadoParaIso: string | null;
  inicioAtendimentoIso: string | null;
  fimAtendimentoIso: string | null;
  status: OsStatus;
  observacoes: string | null;
  clienteAprovou: boolean;
  clienteAprovouEmIso: string | null;
  criadoEmIso: string;
  atualizadoEmIso: string;
  /**
   * FEAT-V2-035 / BUG-V2-047: soft delete (true=ativo). Backend pré-migration
   * 051 envia true por compat (shape() trata coluna ausente).
   */
  ativo?: boolean;
  /**
   * BUG-V2-058 (2026-05-25): tags pré-carregadas pelo backend pra render no
   * header do OsCard (regra 13 padrão Kanban). Shape compatível com TagBadge.
   */
  tags?: Array<{
    id: string;
    nome: string;
    cor: string;
    icone: string | null;
    ativo: boolean;
  }>;
}

export interface CreateOsInput {
  workflowId: string;
  clienteId: string;
  tecnicoId?: string | null;
  tipo: string;
  titulo: string;
  descricao?: string | null;
  endereco: string;
  latitude?: number | null;
  longitude?: number | null;
  prioridade?: OsPrioridade;
  agendadoParaIso?: string | null;
  observacoes?: string | null;
}


// ChecklistTemplate / ChecklistResponse REMOVIDOS na Rodada Polish OS
// Digital Bloco B. Substitutos em `src/lib/questionarios-store.ts`:
//   - QuestionarioTemplate / QuestionarioPergunta / QuestionarioResposta
//   - useQuestionarioTemplates / useQuestionarioPerguntas / useQuestionarioRespostas
//   - useQuestionarioActions (CRUD admin) / useSubmitRespostas (técnico)

export interface OsSignature {
  id: string;
  osId: string;
  signerName: string;
  signerRole: 'cliente' | 'tecnico';
  signatureSvg: string;
  assinadoEmIso: string;
  ipAddress: string | null;
  latitude: number | null;
  longitude: number | null;
}

export interface OsExternalLink {
  token: string;
  osId: string;
  expiresEmIso: string | null;
  criadoPorId: string | null;
  criadoEmIso: string;
  acessos: number;
  ultimoAcessoIso: string | null;
  /** IP do último acesso (audit log — Bloco A / migration 026). */
  ultimoIp: string | null;
  /** User-Agent do último acesso (audit log — Bloco A / migration 026). */
  ultimoUa: string | null;
  ativo: boolean;
}

// ─── Keys ───────────────────────────────────────────────────────────────

export const OS_KEYS = {
  all: ['v1', 'service-orders'] as const,
  list: (filters: Record<string, unknown>) => ['v1', 'service-orders', { filters }] as const,
  byId: (id: string) => ['v1', 'service-orders', id] as const,
  checklists: (osId: string) => ['v1', 'service-orders', osId, 'checklist'] as const,
  externalLinks: (osId: string) => ['v1', 'service-orders', osId, 'external-link'] as const,
  templates: (workflowId: string | null) =>
    ['v1', 'checklist-templates', { workflowId }] as const,
};

// ─── Queries ────────────────────────────────────────────────────────────

interface UseOsListOptions {
  workflowId?: string | null;
  status?: OsStatus | null;
  tecnicoId?: string | null;
  clienteId?: string | null;
  mine?: boolean;
}

export function useServiceOrders(options: UseOsListOptions = {}) {
  const { workflowId, status, tecnicoId, clienteId, mine } = options;
  const params = new URLSearchParams();
  if (workflowId) params.set('workflow_id', workflowId);
  if (status) params.set('status', status);
  if (tecnicoId) params.set('tecnico_id', tecnicoId);
  if (clienteId) params.set('cliente_id', clienteId);
  if (mine) params.set('mine', '1');
  const qs = params.toString();
  const url = qs ? `/api/v1/service-orders?${qs}` : '/api/v1/service-orders';
  return useQuery({
    queryKey: OS_KEYS.list({ workflowId, status, tecnicoId, clienteId, mine }),
    queryFn: () => api.get<ServiceOrder[]>(url),
    enabled: hasToken(),
    staleTime: 30_000,
  });
}

export function useServiceOrder(id: string | null) {
  return useQuery({
    queryKey: OS_KEYS.byId(id ?? ''),
    queryFn: () => api.get<ServiceOrder>(`/api/v1/service-orders/${encodeURIComponent(id!)}`),
    enabled: !!id && hasToken(),
    staleTime: 15_000,
  });
}

// useChecklistTemplates / useOsChecklistResponses REMOVIDOS — substitutos:
//   - useQuestionarioTemplates / useQuestionarioPerguntas / useQuestionarioRespostas
// em `src/lib/questionarios-store.ts`.

export function useOsExternalLinks(osId: string | null) {
  return useQuery({
    queryKey: OS_KEYS.externalLinks(osId ?? ''),
    queryFn: () =>
      api.get<OsExternalLink[]>(
        `/api/v1/service-orders/${encodeURIComponent(osId!)}/external-link`
      ),
    enabled: !!osId && hasToken(),
    // BUG-V2-038 (2026-05-24): modal de "Gerar link público" é montado
    // condicionalmente. Cache stale de uma sessão anterior (lista vazia)
    // estava sendo exibida imediatamente no remount, e `isLoading=false`
    // (cache hit) escondia o estado "Carregando…". Resultado: usuário via
    // "Nenhum link gerado ainda" mesmo quando havia 3 links no DB.
    //
    // Fix: staleTime=0 + refetchOnMount='always' garantem fetch fresco
    // SEMPRE que o modal abre. Custo: 1 GET extra por abertura — aceitável
    // pra esse fluxo admin/baixa frequência.
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

// ─── Actions ────────────────────────────────────────────────────────────

export function useServiceOrderActions() {
  const qc = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);

  const invalidateAll = () => qc.invalidateQueries({ queryKey: OS_KEYS.all });

  const create = useCallback(
    async (input: CreateOsInput): Promise<ServiceOrder> => {
      setPending('create');
      try {
        const os = await api.post<ServiceOrder>('/api/v1/service-orders', input);
        invalidateAll();
        return os;
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc]
  );

  const changeStatus = useCallback(
    async (
      id: string,
      status: OsStatus,
      opts?: { latitude?: number; longitude?: number }
    ): Promise<ServiceOrder> => {
      setPending(id);
      try {
        const os = await api.put<ServiceOrder>(
          `/api/v1/service-orders/${encodeURIComponent(id)}/status`,
          { status, ...(opts ?? {}) }
        );
        invalidateAll();
        return os;
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc]
  );

  // submitChecklist REMOVIDO — substituído por `useSubmitRespostas` em
  // `src/lib/questionarios-store.ts` (mais explícito + invalida outra queryKey).

  const submitSignature = useCallback(
    async (
      osId: string,
      input: { signerName: string; signerRole: 'cliente' | 'tecnico'; signatureSvg: string; latitude?: number; longitude?: number }
    ): Promise<OsSignature> => {
      setPending(`sig-${osId}`);
      try {
        const s = await api.post<OsSignature>(
          `/api/v1/service-orders/${encodeURIComponent(osId)}/signature`,
          input
        );
        // Cliente signing → marca clienteAprovou no backend, invalida OS query
        if (input.signerRole === 'cliente') invalidateAll();
        return s;
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc]
  );

  const createExternalLink = useCallback(
    async (
      osId: string,
      opts?: { expiresEmIso?: string | null }
    ): Promise<{ token: string; publicUrl: string }> => {
      setPending(`link-${osId}`);
      try {
        const r = await api.post<{ token: string; publicUrl: string }>(
          `/api/v1/service-orders/${encodeURIComponent(osId)}/external-link`,
          opts ?? {}
        );
        qc.invalidateQueries({ queryKey: OS_KEYS.externalLinks(osId) });
        return r;
      } finally {
        setPending(null);
      }
    },
    [qc]
  );

  const deleteExternalLink = useCallback(
    async (osId: string, token: string): Promise<void> => {
      setPending(`link-del-${token}`);
      try {
        await api.delete(
          `/api/v1/service-orders/${encodeURIComponent(osId)}/external-link/${encodeURIComponent(token)}`
        );
        qc.invalidateQueries({ queryKey: OS_KEYS.externalLinks(osId) });
      } finally {
        setPending(null);
      }
    },
    [qc]
  );

  return {
    create,
    changeStatus,
    // submitChecklist removido — useSubmitRespostas em questionarios-store
    submitSignature,
    createExternalLink,
    deleteExternalLink,
    pending,
  };
}
